import { ContextMenu as BaseContextMenu } from '@base-ui/react/context-menu';
import type { ComponentProps } from 'react';
import { CheckIcon, ChevronRightIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx, styled } from '../lib/styled.tsx';
import {
  groupLabelSurface,
  itemSurface,
  popupSeparator,
  popupSurface,
  popupTransition,
} from '../lib/surfaces.ts';

const popup =
  `${popupSurface} ${popupTransition} ` +
  'min-w-60 max-h-[var(--available-height)] overflow-y-auto p-2';

/**
 * A tick by default, so the common checkbox/radio row needs no extra markup.
 *
 * Kept mounted and merely hidden when unchecked, so every row in a menu shares
 * one column for its tick: without it, unchecked labels slide left.
 */
function CheckboxItemIndicator({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseContextMenu.CheckboxItemIndicator>) {
  return (
    <BaseContextMenu.CheckboxItemIndicator
      keepMounted
      className={cx(
        'flex size-4 shrink-0 items-center justify-center text-brand-600 data-unchecked:invisible',
        className,
      )}
      {...props}
    >
      {children ?? <CheckIcon className="size-3.5" />}
    </BaseContextMenu.CheckboxItemIndicator>
  );
}

function RadioItemIndicator({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseContextMenu.RadioItemIndicator>) {
  return (
    <BaseContextMenu.RadioItemIndicator
      keepMounted
      className={cx(
        'flex size-4 shrink-0 items-center justify-center text-brand-600 data-unchecked:invisible',
        className,
      )}
      {...props}
    >
      {children ?? <span className="size-1.5 rounded-full bg-current" />}
    </BaseContextMenu.RadioItemIndicator>
  );
}

function SubmenuTrigger({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseContextMenu.SubmenuTrigger>) {
  return (
    <BaseContextMenu.SubmenuTrigger
      className={cx(itemSurface, 'justify-between data-popup-open:bg-surface-muted', className)}
      {...props}
    >
      {children}
      <ChevronRightIcon className="size-3.5 shrink-0 text-ink-muted" />
    </BaseContextMenu.SubmenuTrigger>
  );
}

/**
 * The same rows as {@link Menu}, raised by a right-click on a region instead of
 * a button. `Trigger` wraps that region rather than replacing it.
 *
 * Anything offered here has to exist somewhere else too: there is no way to
 * reach a context menu by touch or keyboard alone on every platform.
 */
export const ContextMenu = {
  Root: BaseContextMenu.Root,
  Trigger: styled(BaseContextMenu.Trigger, ''),
  Portal: BaseContextMenu.Portal,
  Backdrop: styled(BaseContextMenu.Backdrop, 'fixed inset-0'),
  Positioner: styled(BaseContextMenu.Positioner, `${LAYER.popup} outline-none`),
  Popup: styled(BaseContextMenu.Popup, popup),
  Arrow: styled(BaseContextMenu.Arrow, ''),
  Item: styled(BaseContextMenu.Item, itemSurface),
  LinkItem: styled(BaseContextMenu.LinkItem, `${itemSurface} no-underline`),
  Separator: styled(BaseContextMenu.Separator, popupSeparator),
  Group: styled(BaseContextMenu.Group, ''),
  GroupLabel: styled(BaseContextMenu.GroupLabel, groupLabelSurface),
  RadioGroup: styled(BaseContextMenu.RadioGroup, ''),
  RadioItem: styled(BaseContextMenu.RadioItem, itemSurface),
  RadioItemIndicator,
  CheckboxItem: styled(BaseContextMenu.CheckboxItem, itemSurface),
  CheckboxItemIndicator,
  SubmenuRoot: BaseContextMenu.SubmenuRoot,
  SubmenuTrigger,
};
